// Modal de cadastro rápido de fornecedor (sub-modal de ModalNovaDespesa /
// ModalDetalheCP), que também EDITA um fornecedor existente.
// Extraído de FinanceiroContasPagar.tsx durante refactor topológico.
//
// 31/08/2026 — dois pedidos que são o mesmo problema:
//  · a Daniele quer buscar o CNPJ na Receita ao cadastrar, como em Clientes;
//  · o Lucas não distingue duas filiais da mesma empresa na hora de escolher.
// São as duas pontas: 939 dos 951 fornecedores estão SEM CNPJ, então o rótulo
// da lista não tem o que mostrar. A busca preenche na criação; a edição
// conserta quem já está lá.
import { useState } from 'react';
import axios from 'axios';
import { Modal, Form, Input, Row, Col, Alert, App as AntApp } from 'antd';
import { API_URL } from '../../config';
import { getToken } from './utils';
import BuscaCnpjBox from '../BuscaCnpjBox';
import { formatarCnpjCpf, rotuloFornecedor, FornecedorSlim } from '../../utils/fornecedores';

export function ModalNovoFornecedor({ fornecedor, comObservacoes, onFechar, onCriou }: {
  /** Presente = modo edição. Ausente = cadastro novo. */
  fornecedor?: FornecedorSlim & { email?: string | null; telefone?: string | null; observacoes?: string | null };
  /**
   * 01/10/2026 — só a aba Fornecedores (Financeiro → Cadastros) liga isto. Mostra
   * o campo Observações E o manda no PATCH/POST. Quem abre o modal pelo Contas a
   * Pagar não passa a prop: o campo não aparece e `observacoes` NÃO vai no corpo
   * (a lista do CP não carrega o texto; mandá-lo vazio apagaria o que existe).
   */
  comObservacoes?: boolean;
  onFechar: () => void;
  onCriou: (id: string) => void;
}) {
  const { message } = AntApp.useApp();
  const editando = !!fornecedor?.id;
  const [form, setForm] = useState({
    nome_fantasia: fornecedor?.nome_fantasia || '',
    razao_social: fornecedor?.razao_social || '',
    cnpj_cpf: formatarCnpjCpf(fornecedor?.cnpj_cpf) || '',
    email: fornecedor?.email || '',
    telefone: fornecedor?.telefone || '',
    observacoes: fornecedor?.observacoes || '',
  });
  const [salvando, setSalvando] = useState(false);
  // Quem já existe com este CNPJ ou com este nome. O servidor recusa CNPJ
  // repetido (409), mas nome repetido ele aceita — e é exatamente o que produz
  // duas linhas idênticas na lista. Avisar ANTES vale mais que recusar depois.
  const [jaExiste, setJaExiste] = useState<string | null>(null);

  async function conferirDuplicata(cnpj: string, nome: string) {
    const termo = (cnpj || '').replace(/\D/g, '') || (nome || '').trim();
    if (termo.length < 3) { setJaExiste(null); return; }
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/fornecedores?ativo=true&limit=20&busca=${encodeURIComponent(termo)}`,
        { headers: { Authorization: token } });
      const lista = (r.data.body ? JSON.parse(r.data.body) : r.data) as FornecedorSlim[];
      const outros = (Array.isArray(lista) ? lista : []).filter(f => String(f.id) !== String(fornecedor?.id || ''));
      setJaExiste(outros.length ? outros.slice(0, 3).map(f => rotuloFornecedor(f, { avisarSemCnpj: true })).join(' · ') : null);
    } catch { setJaExiste(null); }
  }

  function aplicarDadosRfb(d: any) {
    setForm(f => ({
      ...f,
      // A Receita devolve nome fantasia vazio para muita empresa — aí o nome
      // que a pessoa vai procurar na lista é a razão social mesmo.
      nome_fantasia: f.nome_fantasia || d.nome_fantasia || d.razao_social || '',
      razao_social: d.razao_social || f.razao_social,
      cnpj_cpf: d.cnpj || f.cnpj_cpf,
      email: f.email || d.contato_email || '',
      telefone: f.telefone || d.contato_fone || '',
    }));
    conferirDuplicata(d.cnpj, d.razao_social || '');
  }

  async function salvar() {
    if (!form.nome_fantasia.trim()) { message.warning('Nome fantasia obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const { observacoes, ...basico } = form;
      const corpo = {
        ...basico,
        cnpj_cpf: form.cnpj_cpf.trim() || null,
        // Só viaja se a pessoa REALMENTE mexeu: o servidor escreve o rastro das
        // junções aqui ("Juntado em …"), e salvar de volta o texto que a tela
        // carregou minutos atrás apagaria o rastro de quem juntou nesse meio-tempo.
        ...(comObservacoes && observacoes.trim() !== (fornecedor?.observacoes || '').trim()
          ? { observacoes: observacoes.trim() || null } : {}),
      };
      const r = editando
        ? await axios.patch(`${API_URL}/fornecedores/${fornecedor!.id}`, corpo,
            { headers: { Authorization: token } })
        : await axios.post(`${API_URL}/fornecedores`, corpo,
            { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(editando ? 'Fornecedor atualizado.' : 'Fornecedor cadastrado.');
      onCriou(String(d?.id || fornecedor?.id || ''));
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message), 6);
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onFechar} onOk={salvar} confirmLoading={salvando} width={560} destroyOnClose
      centered title={editando ? '✎ Editar fornecedor' : '+ Novo fornecedor'}
      okText={editando ? 'Salvar' : 'Cadastrar'} cancelText="Cancelar">
      {/* Mesma caixa do cadastro de Clientes — o pedido da Daniele era esse. */}
      <BuscaCnpjBox cnpjInicial={form.cnpj_cpf} onEncontrado={aplicarDadosRfb} />
      {jaExiste && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Já existe fornecedor parecido"
          description={<span style={{ fontSize: 12 }}>{jaExiste}</span>} />
      )}
      <Form layout="vertical" size="small">
        <Form.Item label="Nome fantasia" required
          extra="É este nome que aparece na lista do Contas a Pagar.">
          <Input value={form.nome_fantasia} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })}
            onBlur={e => conferirDuplicata(form.cnpj_cpf, e.target.value)}
            placeholder="ex: Petshop Brutus" />
        </Form.Item>
        <Form.Item label="Razão social">
          <Input value={form.razao_social} onChange={e => setForm({ ...form, razao_social: e.target.value })} />
        </Form.Item>
        <Row gutter={10}>
          <Col span={12}>
            <Form.Item label="CNPJ/CPF"
              extra="Sem ele, duas filiais da mesma empresa ficam idênticas na lista.">
              <Input value={form.cnpj_cpf}
                onChange={e => setForm({ ...form, cnpj_cpf: e.target.value })}
                onBlur={e => {
                  const v = formatarCnpjCpf(e.target.value);
                  setForm(f => ({ ...f, cnpj_cpf: v }));
                  conferirDuplicata(v, form.nome_fantasia);
                }}
                placeholder="00.000.000/0000-00" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Telefone">
              <Input value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Email">
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </Form.Item>
          </Col>
          {comObservacoes && (
            <Col span={24}>
              <Form.Item label="Observações">
                <Input.TextArea rows={3} value={form.observacoes}
                  onChange={e => setForm({ ...form, observacoes: e.target.value })} />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
}
